import { createAsyncThunk } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { api } from "../../api/api";
import { ERROR_MESSAGES } from "../../constants/enums";
import { showMessage } from "../messages/messagesActions";
import { IStoreState } from "../initialStoreState";
import { ICommAutoDriverProfile } from "./commAutoDriverProfile.types";

export const fetchCommAutoDriverProfileListAsync = createAsyncThunk<
  { data: ICommAutoDriverProfile[]; totalRecords: number },
  {
    customerPolicyId: number;
    pageNumber: number;
    rowPerPage: number;
    status?: string;
  }
>(
  "commAutoDriverProfile/fetchCommAutoDriverProfileListAsync",
  async ({ customerPolicyId, pageNumber, rowPerPage, status }, { rejectWithValue }) => {
    try {
      let url = `/policy/get-policy-commauto-driver-profile?customer_policy_id=${customerPolicyId}&pageNo=${pageNumber}&itemPerPage=${rowPerPage}`;
      if (status && status !== "-1") {
        url += `&status=${status}`;
      }
      const res = await api.get(url);
      const data: ICommAutoDriverProfile[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      return { data: data || [], totalRecords: totalRecords || 0 };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const fetchCommAutoDriverProfileByIdAsync = createAsyncThunk<
  ICommAutoDriverProfile,
  { customerPolicyId: number; incrementalNumber: number }
>(
  "commAutoDriverProfile/fetchCommAutoDriverProfileByIdAsync",
  async ({ customerPolicyId, incrementalNumber }, { rejectWithValue }) => {
    try {
      const url = `/policy/get-policy-commauto-driver-profile?customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}`;
      const res = await api.get(url);
      const data: ICommAutoDriverProfile[] = res.data.data;
      if (data && data.length > 0) {
        return data[0];
      }
      return rejectWithValue("No data found");
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const upsertCommAutoDriverProfileAsync = createAsyncThunk<
  ICommAutoDriverProfile,
  ICommAutoDriverProfile
>(
  "commAutoDriverProfile/upsertCommAutoDriverProfileAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post("/policy/edit-policy-commauto-driver-profile", payload);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const downloadCommAutoDriverProfilePDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        `/policy/get-policy-commauto-driver-history-supplementary-form-pdf`,
        {
          customer_policy_id: customerPolicyId,
          incremental_number: incrementalNumber,
        },
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `driver_profile_${incrementalNumber}.pdf`;
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback();
    }
  };

export const previewCommAutoDriverProfilePDFAsync =
  (
    customerPolicyId: number,
    incrementalNumber: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        `/policy/get-policy-commauto-driver-history-supplementary-form-pdf`,
        {
          customer_policy_id: customerPolicyId,
          incremental_number: incrementalNumber,
          is_policy_commauto_driver_history_supplementary_form_preview: true,
        }
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        })
      );
      onCallback();
    }
  };
